import { clsx } from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';

import styles from './bannerCarousel.module.css';
import type { BannerCarouselProps } from './bannerCarousel.types';
import { BannerPagination } from './bannerPagination/bannerPagination';
import { CarouselItem } from './carouselItem/carouselItem';

const DEFAULT_AUTO_ADVANCE_MS = 7000;

export const BannerCarousel = ({
  slides,
  autoAdvanceMs = DEFAULT_AUTO_ADVANCE_MS,
  disableAutoAdvance = false,
  disableKeyboardNavigation = false,
  ariaLabel,
  className,
}: BannerCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const totalItems = slides.length;

  const nextIndex = (activeIndex + 1) % totalItems;
  const prevIndex = (activeIndex + totalItems - 1) % totalItems;

  const startAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (disableAutoAdvance) {
      return;
    }

    intervalRef.current = setInterval(() => {
      setActiveIndex((previousIndex) => (previousIndex + 1) % totalItems);
    }, autoAdvanceMs);
  }, [autoAdvanceMs, disableAutoAdvance, totalItems]);

  const updateIndex = useCallback(
    (newIndex: number) => {
      setActiveIndex(newIndex);
      startAutoSlide(); // reset interval when index is manually updated
    },
    [startAutoSlide]
  );

  useEffect(() => {
    startAutoSlide();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startAutoSlide]);

  const touchHandlers = useSwipeable({
    onSwipedLeft: () => {
      updateIndex(nextIndex);
    },
    onSwipedRight: () => {
      updateIndex(prevIndex);
    },
  });

  useEffect(() => {
    if (disableKeyboardNavigation) {
      return;
    }

    const arrowKeyHandlers = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        updateIndex(prevIndex);
      }

      if (event.key === 'ArrowRight') {
        updateIndex(nextIndex);
      }
    };

    window.addEventListener('keyup', arrowKeyHandlers);

    return () => {
      window.removeEventListener('keyup', arrowKeyHandlers);
    };
  }, [disableKeyboardNavigation, nextIndex, prevIndex, updateIndex]);

  const onCarouselItemClick = (index: number) => () => {
    updateIndex(index);
  };

  return (
    <div
      className={clsx(styles['carouselWrapper'], className)}
      aria-label={ariaLabel}
    >
      <div
        className={styles['carouselTrack']}
        {...touchHandlers}
        style={{ transform: `translateX(-${activeIndex * 100}%)` }}
      >
        {slides.map((slide, index) => (
          <CarouselItem
            key={index}
            activeIndex={activeIndex}
            imageSrc={slide.imageSrc}
            imageAlt={slide.imageAlt}
            srcSet={slide.srcSet}
            fallbackSrc={slide.fallbackSrc}
            onClick={onCarouselItemClick(index)}
            url={slide.url}
            index={index}
          />
        ))}
      </div>
      {totalItems > 1 && (
        <BannerPagination
          activeIndex={activeIndex}
          totalItems={totalItems}
          updateIndex={updateIndex}
        />
      )}
    </div>
  );
};
